"use client";

import { c as _c } from "react/compiler-runtime";
import EnsAddressInput from "@/components/utils/input/ens-address/EnsAddressInput";
import React, { useCallback, useState } from "react";
import { TraitWrapper } from "../../traits/TraitWrapper";
import type { PaymentInfo } from "../types/OperationalData";
import FormSection from "../ui/FormSection";
import MetadataLengthHint from "../ui/MetadataLengthHint";
import { validateStrictAddress } from "../utils/addressValidation";
import type { MetadataValueLengthStatus } from "../utils/submissionMetadata";
interface PaymentConfigProps {
  readonly paymentInfo: PaymentInfo;
  readonly onPaymentInfoChange: (paymentInfo: PaymentInfo) => void;
  readonly paymentInfoLengthStatus?: MetadataValueLengthStatus | undefined;
}
const PaymentConfig: React.FC<PaymentConfigProps> = t0 => {
  const $ = _c(39);
  const {
    paymentInfo,
    onPaymentInfoChange,
    paymentInfoLengthStatus
  } = t0;
  const [isLoading, setIsLoading] = useState(false);
  const [hasEnsError, setHasEnsError] = useState(false);
  let t1;
  if ($[0] !== hasEnsError) {
    t1 = address => {
      if (hasEnsError) {
        return "Could not resolve ENS name";
      }
      if (!address) {
        return null;
      }
      return validateStrictAddress(address) ? null : "Invalid address (0x... 42 chars)";
    };
    $[0] = hasEnsError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getAddressError = t1;
  let t2;
  if ($[2] !== onPaymentInfoChange || $[3] !== paymentInfo) {
    t2 = resolvedAddress => {
      onPaymentInfoChange({
        ...paymentInfo,
        payment_address: resolvedAddress
      });
    };
    $[2] = onPaymentInfoChange;
    $[3] = paymentInfo;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleAddressChange = t2;
  let t3;
  if ($[5] !== onPaymentInfoChange || $[6] !== paymentInfo) {
    t3 = e => {
      const checked = e.target.checked;
      onPaymentInfoChange({
        ...paymentInfo,
        has_designated_payee: checked,
        designated_payee_name: checked ? paymentInfo.designated_payee_name : ""
      });
    };
    $[5] = onPaymentInfoChange;
    $[6] = paymentInfo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleDesignatedPayeeToggle = t3;
  let t4;
  if ($[8] !== onPaymentInfoChange || $[9] !== paymentInfo) {
    t4 = e_0 => {
      onPaymentInfoChange({
        ...paymentInfo,
        designated_payee_name: e_0.target.value
      });
    };
    $[8] = onPaymentInfoChange;
    $[9] = paymentInfo;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleDesignatedPayeeNameChange = t4;
  const address_0 = paymentInfo.payment_address;
  const hasDesignatedPayee = paymentInfo.has_designated_payee;
  const designatedPayeeName = paymentInfo.designated_payee_name;
  const addressError = getAddressError(address_0);
  const isAddressValid = !!address_0 && !addressError && !isLoading;
  const designatedPayeeNameError = hasDesignatedPayee && !designatedPayeeName.trim() ? "Designated payee name is required" : null;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="tw-mb-5 tw-text-[13px] tw-text-iron-400">Address to receive split of minting proceeds.</p>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== paymentInfoLengthStatus) {
    t6 = <MetadataLengthHint status={paymentInfoLengthStatus} className="tw-mb-4" />;
    $[12] = paymentInfoLengthStatus;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== handleDesignatedPayeeToggle || $[15] !== hasDesignatedPayee) {
    t7 = <input type="checkbox" checked={hasDesignatedPayee} onChange={handleDesignatedPayeeToggle} aria-label="Designated Payee" className="tw-form-checkbox tw-h-4 tw-w-4 tw-cursor-pointer tw-rounded tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-800 tw-text-primary-400 focus:tw-ring-primary-400 focus:tw-ring-offset-0 desktop-hover:group-hover:tw-border-iron-400" />;
    $[14] = handleDesignatedPayeeToggle;
    $[15] = hasDesignatedPayee;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="tw-text-[13px] tw-text-iron-400"><span className="tw-font-medium tw-text-iron-200 tw-transition-colors desktop-hover:group-hover:tw-text-white">Designated Payee</span><span className="tw-ml-1 tw-text-iron-400">— proceeds will be paid to a third party</span></span>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7) {
    t9 = <div className="tw-mb-5"><label className="tw-group tw-flex tw-w-max tw-cursor-pointer tw-items-center tw-gap-3">{t7}{t8}</label></div>;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== designatedPayeeName || $[21] !== designatedPayeeNameError || $[22] !== handleDesignatedPayeeNameChange || $[23] !== hasDesignatedPayee) {
    t10 = hasDesignatedPayee && <TraitWrapper label="Designated Payee Name" id="designated-payee-name" error={designatedPayeeNameError} isFieldFilled={!!designatedPayeeName.trim()} showRequiredMarker={true} className="tw-pb-8"><input type="text" value={designatedPayeeName} onChange={handleDesignatedPayeeNameChange} placeholder="Enter designated payee name" autoFocus={true} className={`tw-form-input tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-base tw-text-iron-100 tw-outline-none tw-ring-1 focus:tw-bg-iron-900 focus:tw-text-iron-100 sm:tw-text-sm ${designatedPayeeNameError ? "tw-ring-red" : "tw-ring-iron-700"} focus:tw-ring-primary-400`} /></TraitWrapper>;
    $[20] = designatedPayeeName;
    $[21] = designatedPayeeNameError;
    $[22] = handleDesignatedPayeeNameChange;
    $[23] = hasDesignatedPayee;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = hasDesignatedPayee ? "Designated Payee Address" : "Payment Address";
  const t12 = `tw-form-input tw-w-full tw-truncate tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-3 tw-pl-4 tw-pr-11 tw-text-base tw-text-iron-100 tw-outline-none tw-ring-1 focus:tw-bg-iron-900 focus:tw-text-iron-100 sm:tw-text-sm ${addressError ? "tw-ring-red" : "tw-ring-iron-700"} focus:tw-ring-primary-400`;
  let t13;
  if ($[25] !== address_0 || $[26] !== handleAddressChange || $[27] !== t12) {
    t13 = <EnsAddressInput value={address_0} placeholder="0x... or ENS" onAddressChange={handleAddressChange} onLoadingChange={setIsLoading} onError={setHasEnsError} className={t12} />;
    $[25] = address_0;
    $[26] = handleAddressChange;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== addressError || $[30] !== isAddressValid || $[31] !== t11 || $[32] !== t13) {
    t14 = <TraitWrapper label={t11} id="payment-address" error={addressError} isFieldFilled={isAddressValid} showRequiredMarker={true} labelTone="muted" className="tw-pb-0">{t13}</TraitWrapper>;
    $[29] = addressError;
    $[30] = isAddressValid;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t10 || $[35] !== t14 || $[36] !== t6 || $[37] !== t9) {
    t15 = <FormSection title="Payment" contentClassName="-tw-mt-3">{t5}{t6}{t9}{t10}{t14}</FormSection>;
    $[34] = t10;
    $[35] = t14;
    $[36] = t6;
    $[37] = t9;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
};
export default React.memo(PaymentConfig);